const vatRate = 0.2;

type FeeService = 'management' | 'rentCollection' | 'letOnly' | 'tenantFind';

export type PublishedFee =
  | { readonly percent?: string | null; readonly amountPence?: number | null }
  | null
  | undefined;

export type FeeVatBasis = 'incVat' | 'exVat';

export type FeeAtVatBasisResult =
  | {
      status: 'published';
      percent: number | null;
      amountPence: number | null;
    }
  | {
      status: 'vatNotStated';
      percent: number | null;
      amountPence: number | null;
    }
  | { status: 'notPublished' };

export type ComparableFee =
  | {
      status: 'comparable';
      basis: 'percent';
      frequency: 'perYear' | 'oneOff';
      percentIncVat: number;
      isVatAdded: boolean;
    }
  | {
      status: 'comparable';
      basis: 'flat';
      frequency: 'oneOff';
      amountPenceIncVat: number;
      isVatAdded: boolean;
    }
  | {
      status: 'notComparable';
      reason: 'notPublished' | 'vatNotStated' | 'periodNotStated';
      publishedPercent: number | null;
      publishedAmountPence: number | null;
    };

const percentPattern = /^\d+(\.\d{1,2})?$/;

function roundPercentIncVat(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function applyVatToPercent(percent: number, feesIncludeVat: boolean): number {
  if (feesIncludeVat) {
    return roundPercentIncVat(percent);
  }

  return roundPercentIncVat(percent * (1 + vatRate));
}

function applyVatToPence(amountPence: number, feesIncludeVat: boolean): number {
  if (feesIncludeVat) {
    return amountPence;
  }

  return Math.round(amountPence * (1 + vatRate));
}

function removeVatFromPercent(percent: number): number {
  return roundPercentIncVat(percent / (1 + vatRate));
}

function removeVatFromPence(amountPence: number): number {
  return Math.round(amountPence / (1 + vatRate));
}

function isRecurringService(service: FeeService): boolean {
  return service === 'management' || service === 'rentCollection';
}

function feeFrequency(service: FeeService): 'perYear' | 'oneOff' {
  return isRecurringService(service) ? 'perYear' : 'oneOff';
}

function publishedSnapshot(fee: PublishedFee): {
  publishedPercent: number | null;
  publishedAmountPence: number | null;
} {
  const publishedPercent = parseFeePercent(fee?.percent);
  const rawAmount = fee?.amountPence;
  const publishedAmountPence =
    rawAmount == null || !Number.isFinite(rawAmount) ? null : rawAmount;

  return { publishedPercent, publishedAmountPence };
}

function isValidMonthlyRentPence(
  monthlyRentPence: number | null | undefined,
): monthlyRentPence is number {
  return (
    monthlyRentPence != null &&
    Number.isFinite(monthlyRentPence) &&
    monthlyRentPence > 0
  );
}

export function parseFeePercent(
  percent: string | null | undefined,
): number | null {
  if (percent == null) {
    return null;
  }

  const trimmed = percent.trim().replace(/%$/, '').trim();
  if (!percentPattern.test(trimmed)) {
    return null;
  }

  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 100) {
    return null;
  }

  return value;
}

export function getFeeAtVatBasis(options: {
  fee: PublishedFee;
  feesIncludeVat: boolean | null | undefined;
  vatBasis: FeeVatBasis;
}): FeeAtVatBasisResult {
  const { fee, feesIncludeVat, vatBasis } = options;
  const snapshot = publishedSnapshot(fee);

  if (
    snapshot.publishedPercent == null &&
    snapshot.publishedAmountPence == null
  ) {
    return { status: 'notPublished' };
  }

  if (feesIncludeVat == null) {
    return {
      status: 'vatNotStated',
      percent: snapshot.publishedPercent,
      amountPence: snapshot.publishedAmountPence,
    };
  }

  const storedIsInc = feesIncludeVat;
  const wantInc = vatBasis === 'incVat';

  let percent = snapshot.publishedPercent;
  let amountPence = snapshot.publishedAmountPence;

  if (percent != null) {
    if (storedIsInc && !wantInc) {
      percent = removeVatFromPercent(percent);
    } else if (!storedIsInc && wantInc) {
      percent = applyVatToPercent(percent, false);
    }
  }

  if (amountPence != null) {
    if (storedIsInc && !wantInc) {
      amountPence = removeVatFromPence(amountPence);
    } else if (!storedIsInc && wantInc) {
      amountPence = applyVatToPence(amountPence, false);
    }
  }

  return { status: 'published', percent, amountPence };
}

export function toComparableFee(options: {
  fee: PublishedFee;
  feesIncludeVat: boolean | null | undefined;
  service: FeeService;
}): ComparableFee {
  const { fee, feesIncludeVat, service } = options;
  const snapshot = publishedSnapshot(fee);
  const parsedPercent = parseFeePercent(fee?.percent);

  if (parsedPercent != null) {
    if (feesIncludeVat == null) {
      return {
        status: 'notComparable',
        reason: 'vatNotStated',
        publishedPercent: parsedPercent,
        publishedAmountPence: snapshot.publishedAmountPence,
      };
    }

    return {
      status: 'comparable',
      basis: 'percent',
      frequency: feeFrequency(service),
      percentIncVat: applyVatToPercent(parsedPercent, feesIncludeVat),
      isVatAdded: feesIncludeVat === false,
    };
  }

  const amountPence = fee?.amountPence;
  if (amountPence == null || !Number.isFinite(amountPence)) {
    return {
      status: 'notComparable',
      reason: 'notPublished',
      publishedPercent: null,
      publishedAmountPence: null,
    };
  }

  if (isRecurringService(service)) {
    return {
      status: 'notComparable',
      reason: 'periodNotStated',
      publishedPercent: null,
      publishedAmountPence: amountPence,
    };
  }

  if (feesIncludeVat == null) {
    return {
      status: 'notComparable',
      reason: 'vatNotStated',
      publishedPercent: null,
      publishedAmountPence: amountPence,
    };
  }

  return {
    status: 'comparable',
    basis: 'flat',
    frequency: 'oneOff',
    amountPenceIncVat: applyVatToPence(amountPence, feesIncludeVat),
    isVatAdded: feesIncludeVat === false,
  };
}

export function getFeeCost(options: {
  fee: ComparableFee;
  monthlyRentPence: number | null | undefined;
}): { amountPence: number; frequency: 'perYear' | 'oneOff' } | null {
  const { fee, monthlyRentPence } = options;

  if (fee.status !== 'comparable') {
    return null;
  }

  if (fee.basis === 'flat') {
    return { amountPence: fee.amountPenceIncVat, frequency: fee.frequency };
  }

  if (!isValidMonthlyRentPence(monthlyRentPence)) {
    return null;
  }

  const amountPence = Math.round(
    (fee.percentIncVat / 100) * monthlyRentPence * 12,
  );

  return { amountPence, frequency: fee.frequency };
}

export function getPercentFeeAnnualCost(options: {
  percentIncVat: number;
  monthlyRentPence: number | null | undefined;
}): number | null {
  const { percentIncVat, monthlyRentPence } = options;

  if (!isValidMonthlyRentPence(monthlyRentPence)) {
    return null;
  }

  return Math.round((percentIncVat / 100) * monthlyRentPence * 12);
}

export function compareFeeWithMedian(options: {
  fee: ComparableFee;
  medianPercentIncVat: number;
  monthlyRentPence: number | null | undefined;
}): { differencePence: number | null; differencePoints: number | null } | null {
  const { fee, medianPercentIncVat, monthlyRentPence } = options;

  if (fee.status !== 'comparable') {
    return null;
  }

  const feeCost = getFeeCost({ fee, monthlyRentPence });

  if (fee.basis === 'percent') {
    const differencePoints = roundPercentIncVat(
      fee.percentIncVat - medianPercentIncVat,
    );
    let differencePence: number | null = null;

    if (isValidMonthlyRentPence(monthlyRentPence) && feeCost != null) {
      const medianCostPence = getPercentFeeAnnualCost({
        percentIncVat: medianPercentIncVat,
        monthlyRentPence,
      });
      if (medianCostPence != null) {
        differencePence = feeCost.amountPence - medianCostPence;
      }
    }

    return { differencePence, differencePoints };
  }

  if (feeCost == null) {
    return { differencePence: null, differencePoints: null };
  }

  let differencePence: number | null = null;
  if (isValidMonthlyRentPence(monthlyRentPence)) {
    const medianCostPence = getPercentFeeAnnualCost({
      percentIncVat: medianPercentIncVat,
      monthlyRentPence,
    });
    if (medianCostPence != null) {
      differencePence = feeCost.amountPence - medianCostPence;
    }
  }

  return { differencePence, differencePoints: null };
}

export type AreaFeeService = 'management' | 'rentCollection' | 'letOnly';

const branchFeeKeys = [
  'management',
  'rentCollection',
  'tenantFind',
  'letOnly',
  'renewal',
  'inventory',
  'managementWithTenantInPlace',
  'rentCollectionWithTenantInPlace',
] as const;

export type BranchFees = {
  feesIncludeVat?: boolean | null;
  management?: PublishedFee;
  rentCollection?: PublishedFee;
  tenantFind?: PublishedFee;
  letOnly?: PublishedFee;
  renewal?: PublishedFee;
  inventory?: PublishedFee;
  managementWithTenantInPlace?: PublishedFee;
  rentCollectionWithTenantInPlace?: PublishedFee;
  otherFees?: readonly PublishedFee[] | null;
} | null;

export type ClassifyAreaBranchFeeResult =
  | { status: 'noFees' }
  | { status: 'serviceNotPublished' }
  | {
      status: 'comparable';
      fee: Extract<ComparableFee, { status: 'comparable' }>;
    }
  | {
      status: 'notComparable';
      reason: 'vatNotStated' | 'periodNotStated';
    };

export function getServiceFee(options: {
  fees: BranchFees;
  service: AreaFeeService;
}): PublishedFee {
  const { fees, service } = options;
  if (fees == null) {
    return null;
  }

  if (service === 'management') {
    return fees.management ?? null;
  }

  if (service === 'rentCollection') {
    return fees.rentCollection ?? null;
  }

  const letOnly = fees.letOnly;
  if (
    letOnly?.percent != null ||
    (letOnly?.amountPence != null && Number.isFinite(letOnly.amountPence))
  ) {
    return letOnly;
  }

  return fees.tenantFind ?? null;
}

export function hasAnyPublishedFee(fees: BranchFees): boolean {
  if (fees == null) {
    return false;
  }

  for (const key of branchFeeKeys) {
    const fee = fees[key];
    if (fee?.percent != null || fee?.amountPence != null) {
      return true;
    }
  }

  return (fees.otherFees ?? []).some(
    (fee) => fee?.percent != null || fee?.amountPence != null,
  );
}

export function classifyAreaBranchFee(options: {
  fees: BranchFees;
  service: AreaFeeService;
}): ClassifyAreaBranchFeeResult {
  const { fees, service } = options;

  if (!hasAnyPublishedFee(fees)) {
    return { status: 'noFees' };
  }

  const serviceFee = getServiceFee({ fees, service });
  const snapshot = publishedSnapshot(serviceFee);
  if (
    snapshot.publishedPercent == null &&
    snapshot.publishedAmountPence == null
  ) {
    return { status: 'serviceNotPublished' };
  }

  const feeService: FeeService =
    service === 'rentCollection'
      ? 'rentCollection'
      : service === 'letOnly'
        ? 'letOnly'
        : 'management';

  const comparable = toComparableFee({
    fee: serviceFee,
    feesIncludeVat: fees?.feesIncludeVat,
    service: feeService,
  });

  if (comparable.status === 'comparable') {
    return { status: 'comparable', fee: comparable };
  }

  if (comparable.reason === 'notPublished') {
    return { status: 'serviceNotPublished' };
  }

  return {
    status: 'notComparable',
    reason: comparable.reason,
  };
}

type AreaPercentFeeStats = {
  sampleSize?: number | null;
  vatUnknownCount?: number | null;
  flatFeeCount?: number | null;
  minimumPercentIncVat?: number | null;
  medianPercentIncVat?: number | null;
  maximumPercentIncVat?: number | null;
  percentsIncVat?: readonly number[] | null;
};

type AreaLetOnlyFeeValue = {
  percentIncVat?: number | null;
  amountPenceIncVat?: number | null;
};

type AreaLetOnlyFeeStats = {
  sampleSize?: number | null;
  vatUnknownCount?: number | null;
  fees?: readonly AreaLetOnlyFeeValue[] | null;
};

export type AreaStatsForSummary = {
  management?: AreaPercentFeeStats | null;
  rentCollection?: AreaPercentFeeStats | null;
  letOnly?: AreaLetOnlyFeeStats | null;
  noFeesBranchCount?: number | null;
  vatUnknownBranchCount?: number | null;
  medianGoogleRating?: number | null;
  googleReviewCount?: number | null;
};

export type AreaFeeSummary = {
  comparableCount: number;
  medianPercentIncVat: number | null;
  spreadPence: number | null;
  frequency: 'perYear' | 'oneOff';
};

function medianOfSorted(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    const left = sorted[middle - 1];
    const right = sorted[middle];
    if (left === undefined || right === undefined) {
      return null;
    }

    return (left + right) / 2;
  }

  return sorted[middle] ?? null;
}

function annualRentPence(monthlyRentPence: number): number {
  return monthlyRentPence * 12;
}

function percentSpreadPence(options: {
  minimumPercentIncVat: number;
  maximumPercentIncVat: number;
  monthlyRentPence: number;
}): number {
  const { minimumPercentIncVat, maximumPercentIncVat, monthlyRentPence } =
    options;
  const spreadPoints = maximumPercentIncVat - minimumPercentIncVat;

  return Math.round((spreadPoints / 100) * annualRentPence(monthlyRentPence));
}

function letOnlyFeeToComparable(
  fee: AreaLetOnlyFeeValue,
): ComparableFee | null {
  if (fee.percentIncVat != null && Number.isFinite(fee.percentIncVat)) {
    return {
      status: 'comparable',
      basis: 'percent',
      frequency: 'oneOff',
      percentIncVat: fee.percentIncVat,
      isVatAdded: false,
    };
  }

  if (fee.amountPenceIncVat != null && Number.isFinite(fee.amountPenceIncVat)) {
    return {
      status: 'comparable',
      basis: 'flat',
      frequency: 'oneOff',
      amountPenceIncVat: fee.amountPenceIncVat,
      isVatAdded: false,
    };
  }

  return null;
}

function summarizePercentServiceAreaFees(options: {
  stats: AreaPercentFeeStats | null | undefined;
  monthlyRentPence: number;
}): AreaFeeSummary {
  const { stats, monthlyRentPence } = options;
  const comparableCount = stats?.sampleSize ?? 0;
  const medianPercentIncVat = stats?.medianPercentIncVat ?? null;
  const minimum = stats?.minimumPercentIncVat;
  const maximum = stats?.maximumPercentIncVat;

  let spreadPence: number | null = null;
  if (
    medianPercentIncVat != null &&
    minimum != null &&
    maximum != null &&
    isValidMonthlyRentPence(monthlyRentPence)
  ) {
    spreadPence = percentSpreadPence({
      minimumPercentIncVat: minimum,
      maximumPercentIncVat: maximum,
      monthlyRentPence,
    });
  }

  return {
    comparableCount,
    medianPercentIncVat,
    spreadPence,
    frequency: 'perYear',
  };
}

function summarizeLetOnlyAreaFees(options: {
  stats: AreaLetOnlyFeeStats | null | undefined;
  monthlyRentPence: number;
}): AreaFeeSummary {
  const { stats, monthlyRentPence } = options;
  const comparableCount = stats?.sampleSize ?? 0;
  const fees = stats?.fees ?? [];

  if (
    fees.length < 3 ||
    !isValidMonthlyRentPence(monthlyRentPence) ||
    stats?.fees == null
  ) {
    return {
      comparableCount,
      medianPercentIncVat: null,
      spreadPence: null,
      frequency: 'oneOff',
    };
  }

  const costs = fees.flatMap((fee) => {
    const comparable = letOnlyFeeToComparable(fee);
    if (comparable == null) {
      return [];
    }

    const cost = getFeeCost({ fee: comparable, monthlyRentPence });
    return cost == null ? [] : [cost.amountPence];
  });

  if (costs.length < 3) {
    return {
      comparableCount,
      medianPercentIncVat: null,
      spreadPence: null,
      frequency: 'oneOff',
    };
  }

  const sortedCosts = [...costs].sort((left, right) => left - right);
  const minimumCost = sortedCosts[0] ?? null;
  const maximumCost = sortedCosts[sortedCosts.length - 1] ?? null;
  const medianCost = medianOfSorted(costs);

  let medianPercentIncVat: number | null = null;
  if (medianCost != null) {
    medianPercentIncVat = roundPercentIncVat(
      (medianCost / annualRentPence(monthlyRentPence)) * 100,
    );
  }

  let spreadPence: number | null = null;
  if (minimumCost != null && maximumCost != null) {
    spreadPence = maximumCost - minimumCost;
  }

  return {
    comparableCount,
    medianPercentIncVat,
    spreadPence,
    frequency: 'oneOff',
  };
}

export function summarizeAreaFees(options: {
  stats: AreaStatsForSummary | null | undefined;
  service: AreaFeeService;
  monthlyRentPence: number | null | undefined;
}): AreaFeeSummary {
  const { stats, service, monthlyRentPence } = options;

  if (!isValidMonthlyRentPence(monthlyRentPence)) {
    return {
      comparableCount: 0,
      medianPercentIncVat: null,
      spreadPence: null,
      frequency: service === 'letOnly' ? 'oneOff' : 'perYear',
    };
  }

  if (stats == null) {
    return {
      comparableCount: 0,
      medianPercentIncVat: null,
      spreadPence: null,
      frequency: service === 'letOnly' ? 'oneOff' : 'perYear',
    };
  }

  if (service === 'management') {
    return summarizePercentServiceAreaFees({
      stats: stats.management,
      monthlyRentPence,
    });
  }

  if (service === 'rentCollection') {
    return summarizePercentServiceAreaFees({
      stats: stats.rentCollection,
      monthlyRentPence,
    });
  }

  return summarizeLetOnlyAreaFees({
    stats: stats.letOnly,
    monthlyRentPence,
  });
}

export function getPositionInRange(options: {
  value: number;
  min: number;
  max: number;
}): number {
  const { value, min, max } = options;

  if (max <= min) {
    return 0.5;
  }

  const position = (value - min) / (max - min);
  if (position <= 0) {
    return 0;
  }

  if (position >= 1) {
    return 1;
  }

  return position;
}
